// ── Recordings tab — per-session recording reads (Wave 3, REAL read) ────────
// Reads cohort sessions, then the selected session's recordings via
// useHonestQuery -> CohortTabShell. Session picker is a collection filter.

import { CollectionPagination, CollectionToolbar, DataTableV2 } from "@vjal/ui";
import {  useMemo, useState } from "react";

import { useHonestQuery } from "../../../components/honest-state";
import { getCoachingServices, getCoachingZ6Services } from "../../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../../hooks/coaching-query-keys";
import type { CoachingSessionDto, ListResponse } from "../../../services/coaching-dtos";
import type { CoachingSessionRecordingsDto } from "../../../services/coaching-z6-dtos";
import { CohortTabShell } from "./cohort-tab-shell";
import { mapRecordingEntries, useCohortRecordingsCollection } from "./cohort-recordings-collection";

const services = getCoachingServices();

export function CohortRecordingsTab({ cohortId }: { cohortId: string }) {
  const sessionsQuery = useHonestQuery<ListResponse<CoachingSessionDto>>(
    coachingQueryKeys.list("sessions.listForCohort", { cohort_id: cohortId }),
    () => services.sessions.listForCohort(cohortId)
  );
  const sessions = sessionsQuery.data?.items ?? [];
  const defaultSessionId = sessions[0]?.id ?? "";
  const [activeSessionId, setActiveSessionId] = useState("");

  // Render adjustments, not effects: synchronous setState in effects cascades
  // renders (bug-pack class, 2026-07-06). Guarded => converges in one pass.
  if (defaultSessionId && !activeSessionId) setActiveSessionId(defaultSessionId);

  const recordingsQuery = useHonestQuery<CoachingSessionRecordingsDto>(
    coachingQueryKeys.z6.recordings(activeSessionId || "missing"),
    () => getCoachingZ6Services().recordings.forSession(activeSessionId),
    {
      enabled: Boolean(activeSessionId),
      disabledPosture: "empty",
      isEmpty: (data) => (data.recordings?.entries ?? []).length === 0
    }
  );

  const allRows = useMemo(
    () => mapRecordingEntries(recordingsQuery.data?.recordings.entries ?? [], activeSessionId),
    [recordingsQuery.data?.recordings.entries, activeSessionId]
  );
  const collection = useCohortRecordingsCollection(allRows, sessions, activeSessionId || defaultSessionId);

  const filterSessionId = collection.state.filters.session_id;
  const [prevFilterSessionId, setPrevFilterSessionId] = useState(filterSessionId);
  if (filterSessionId !== prevFilterSessionId) {
    setPrevFilterSessionId(filterSessionId);
    if (typeof filterSessionId === "string" && filterSessionId && filterSessionId !== activeSessionId) {
      setActiveSessionId(filterSessionId);
    }
  }

  if (sessionsQuery.posture !== null || sessions.length === 0) {
    return (
      <CohortTabShell
        title="Recordings"
        subtitle="Session recordings (secure link behind auth; passcode presence only)."
        posture={sessionsQuery.posture ?? "empty"}
        gate={sessionsQuery.gate}
        reference={sessionsQuery.reference}
        onRetry={sessionsQuery.retry}
        emptyTitle="No sessions yet"
        emptyMessage="Recordings appear per session once sessions are held and recordings are available."
      >
        <span />
      </CohortTabShell>
    );
  }

  return (
    <CohortTabShell
      title="Recordings"
      subtitle="Session recordings (secure link behind auth; passcode presence only — the raw passcode is never shown)."
      posture={recordingsQuery.posture}
      gate={recordingsQuery.gate}
      reference={recordingsQuery.reference}
      onRetry={recordingsQuery.retry}
      emptyTitle="No recordings for this session"
      emptyMessage="Recordings will appear here once they are available for the selected session."
    >
      <CollectionToolbar collection={collection} className="rounded-xl border border-line" stableChipNames />
      <div className="rounded-xl border border-line bg-white">
        <DataTableV2 collection={collection} caption="Session recordings" />
        {collection.uiState === "ready" && <CollectionPagination collection={collection} className="px-4" />}
      </div>
    </CohortTabShell>
  );
}